/**
 * The hero's standard look (the prototype's Hero 1, Full photo, Plain).
 * Draws the behavior half's state and calls its actions; holds no rule.
 */

import { partsOf, pieceRoot } from '../../contract/runtime';
import { onLink } from '../../ui/onLink';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import type { HeroRuntime } from './useHero';
import css from './Hero.module.css';
import { contract } from './contract';

const part = partsOf(contract);

/** The name with each "&" set in the accent italic, as the prototype draws "Juniper & Stone". */
function nameParts(name: string) {
  return name.split(/(&)/).map((p, i) => (p === '&' ? <em key={i}>&amp;</em> : p));
}

export function HeroView({ state, actions, options }: HeroRuntime) {
  const photo = options.showPhoto ? state.photoUrl : null;
  const showServices = options.showButtons && state.hasServices && options.servicesLabel;
  const showEvents = options.showButtons && state.hasEvents && options.eventsLabel;
  return (
    <section className={`${css.hero} ${photo ? css.withPhoto : ''}`} aria-labelledby="heroName" {...pieceRoot(contract)} {...part('section')}>
      {photo && (
        <>
          <img className={css.photo} src={photo} alt="" {...part('photo')} />
          <div className={css.scrim} aria-hidden="true" />
        </>
      )}
      <div className={`${shared.wrap} ${css.inner}`}>
        {state.eyebrow && (
          <div className={`${shared.eyebrow} ${css.eyebrow}`} {...part('eyebrow')}>
            {state.eyebrow}
          </div>
        )}
        <h1 className={css.name} id="heroName" {...part('name')}>
          {nameParts(state.name)}
        </h1>
        {state.tagline && (
          <p className={css.lead} {...part('tagline')}>
            {state.tagline}
          </p>
        )}
        {(showServices || showEvents) && (
          <div className={css.ctas} {...part('buttons')}>
            {showServices && (
              <a
                className={`${shared.btn} ${shared.btnPrimary} ${css.primary}`}
                href="/#services"
                onClick={onLink(actions.openServices)}
                {...part('servicesButton')}
              >
                {options.servicesLabel}
                <Icon name="arrow" />
              </a>
            )}
            {showEvents && (
              <a
                className={`${shared.btn} ${shared.btnOutline}`}
                href="/events"
                onClick={onLink(actions.openEvents)}
                {...part('eventsButton')}
              >
                {options.eventsLabel}
              </a>
            )}
          </div>
        )}
      </div>
      {options.showFacts && state.facts.length > 0 && (
        <div className={`${shared.wrap} ${css.facts}`} {...part('facts')}>
          {state.facts.map((f) => (
            <div key={f.id} className={css.fact} {...part('fact')}>
              <div className={css.factK} {...part('factLabel')}>
                {f.label}
              </div>
              <div className={css.factV}>
                {f.href ? (
                  <a href={f.href} onClick={onLink(() => actions.openFact(f.id))} {...part('factValue')}>
                    {f.value}
                  </a>
                ) : (
                  <span {...part('factValue')}>{f.value}</span>
                )}
                {f.note && <small {...part('factNote')}>{f.note}</small>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
